import { supabase } from "@/lovable-demo/runtime/client";
import { getSignedInActor, recordAuditEvent } from "@/lovable-demo/lib/governance/audit";
import { evaluateAuthority, type AuthorityRule } from "@/lovable-demo/lib/governance/authority";
import { captureEvidenceSnapshot, registerEvidence } from "@/lovable-demo/lib/governance/evidence";
import { logLineage } from "@/lovable-demo/lib/governance/lineage";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import { buildRecordFromSignal } from "@/lovable-demo/lib/warranty/seed";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import { prioritise } from "./priority";
import type {
  Disposition,
  DispositionEntry,
  RootCauseEvidence,
  SeverityEntry,
  Signal,
  SignalLink,
} from "./types";

export type SignalResult = { ok: true; recordId?: string } | { ok: false; error: string };

const NOT_RECORDED =
  "This decision was not recorded — the governance audit trail could not be written. Nothing has been saved. Please try again.";

// ---------------------------------------------------------------- reads

type SignalRow = {
  id: string;
  code: string;
  signal_class: string;
  title: string;
  summary: string;
  program_id: string | null;
  component: string;
  supplier_name: string;
  plant: string;
  detected_at: string;
  source_system_id: string | null;
  source_reference: string;
  source_timestamp: string | null;
  magnitude: number;
  magnitude_unit: string;
  trend: unknown;
  trend_direction: string;
  exposure_units: number;
  financial_exposure: number;
  recurrence_count: number;
  safety_relevant: boolean;
  compliance_relevant: boolean;
  customer_impact: number;
  data_confidence: number;
  status: string;
  current_disposition: string | null;
  current_severity: number | null;
  severity_rationale: string;
  ai_suggested_severity: number | null;
  linked_record_id: string | null;
  created_at: string;
  updated_at: string;
};

export function signalFromRow(r: SignalRow): Signal {
  return {
    id: r.id,
    code: r.code,
    signalClass: r.signal_class as Signal["signalClass"],
    title: r.title,
    summary: r.summary,
    programId: r.program_id,
    component: r.component,
    supplierName: r.supplier_name,
    plant: r.plant,
    detectedAt: r.detected_at,
    sourceSystemId: r.source_system_id,
    sourceReference: r.source_reference,
    sourceTimestamp: r.source_timestamp,
    magnitude: Number(r.magnitude),
    magnitudeUnit: r.magnitude_unit,
    trend: Array.isArray(r.trend) ? (r.trend as Signal["trend"]) : [],
    trendDirection: (r.trend_direction as Signal["trendDirection"]) ?? "stable",
    exposureUnits: r.exposure_units,
    financialExposure: Number(r.financial_exposure),
    recurrenceCount: r.recurrence_count,
    safetyRelevant: r.safety_relevant,
    complianceRelevant: r.compliance_relevant,
    customerImpact: r.customer_impact,
    dataConfidence: Number(r.data_confidence),
    status: r.status as Signal["status"],
    currentDisposition: (r.current_disposition as Disposition | null) ?? null,
    currentSeverity: r.current_severity,
    severityRationale: r.severity_rationale,
    aiSuggestedSeverity: r.ai_suggested_severity,
    linkedRecordId: r.linked_record_id,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listSignals(): Promise<Signal[]> {
  const { data, error } = await supabase
    .from("signals")
    .select("*")
    .order("detected_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => signalFromRow(r as SignalRow));
}

export async function getSignal(id: string): Promise<Signal | null> {
  const { data } = await supabase.from("signals").select("*").eq("id", id).maybeSingle();
  return data ? signalFromRow(data as SignalRow) : null;
}

export async function listSignalsForRecord(recordId: string): Promise<Signal[]> {
  const { data } = await supabase.from("signals").select("*").eq("linked_record_id", recordId);
  return (data ?? []).map((r) => signalFromRow(r as SignalRow));
}

export async function listSignalLinks(signalId: string): Promise<SignalLink[]> {
  const { data } = await supabase
    .from("signal_links")
    .select("*")
    .or(`signal_id.eq.${signalId},related_signal_id.eq.${signalId}`);
  return (data ?? []).map((r) => ({
    id: r.id,
    signalId: r.signal_id,
    relatedSignalId: r.related_signal_id,
    claimId: r.claim_id,
    linkType: r.link_type as SignalLink["linkType"],
    rationale: r.rationale,
    createdBy: r.created_by,
  }));
}

export async function listRootCause(signalIds: string[]): Promise<RootCauseEvidence[]> {
  if (!signalIds.length) return [];
  const { data } = await supabase
    .from("signal_root_cause_evidence")
    .select("*")
    .in("signal_id", signalIds);
  return (data ?? []).map((r) => ({
    id: r.id,
    signalId: r.signal_id,
    statement: r.statement,
    source: r.source,
    verifiedBy: r.verified_by,
    verificationMethod: r.verification_method,
    evidenceRef: r.evidence_ref,
    verifiedAt: r.verified_at,
    carriedToRecordId: r.carried_to_record_id,
  }));
}

export async function listDispositions(signalIds: string[]): Promise<DispositionEntry[]> {
  if (!signalIds.length) return [];
  const { data } = await supabase
    .from("signal_dispositions")
    .select("*")
    .in("signal_id", signalIds)
    .order("created_at", { ascending: false });
  return (data ?? []).map((r) => ({
    id: r.id,
    signalId: r.signal_id,
    disposition: r.disposition as Disposition,
    rationale: r.rationale,
    targetRecordId: r.target_record_id,
    decidedByLabel: r.decided_by_label,
    authorityRuleKey: r.authority_rule_key,
    auditEventId: r.audit_event_id,
    evidenceSnapshot: (r.evidence_snapshot ?? {}) as Record<string, unknown>,
    createdAt: r.created_at,
  }));
}

export async function listSeverity(signalId: string): Promise<SeverityEntry[]> {
  const { data } = await supabase
    .from("signal_severity_assessments")
    .select("*")
    .eq("signal_id", signalId)
    .order("created_at", { ascending: false });
  return (data ?? []).map((r) => ({
    id: r.id,
    signalId: r.signal_id,
    severity: r.severity,
    rationale: r.rationale,
    aiSuggested: r.ai_suggested,
    assessedByLabel: r.assessed_by_label,
    authorityRuleKey: r.authority_rule_key,
    secondApprovalRequired: r.second_approval_required,
    createdAt: r.created_at,
  }));
}

export async function loadAuthorityContext(): Promise<{
  roles: AppRole[];
  rules: AuthorityRule[];
}> {
  const { fetchMyRoles } = await import("@/lovable-demo/lib/governance/roles");
  const [roles, rules] = await Promise.all([
    fetchMyRoles(),
    supabase.from("decision_authority_rules").select("*").eq("active", true),
  ]);
  return { roles, rules: (rules.data ?? []) as unknown as AuthorityRule[] };
}

// ---------------------------------------------------------------- Class A writes

function signalSnapshot(signal: Signal, extra: Record<string, unknown>) {
  const priority = prioritise(signal);
  return {
    capturedAt: new Date().toISOString(),
    signal: {
      code: signal.code,
      class: signal.signalClass,
      status: signal.status,
      severity: signal.currentSeverity,
      severityRationale: signal.severityRationale,
      magnitude: signal.magnitude,
      magnitudeUnit: signal.magnitudeUnit,
      trendDirection: signal.trendDirection,
      exposureUnits: signal.exposureUnits,
      financialExposure: signal.financialExposure,
      dataConfidence: signal.dataConfidence,
      source: signal.sourceSystemId,
      sourceReference: signal.sourceReference,
    },
    priority: {
      score: priority.score,
      factors: priority.factors.map((f) => [f.key, Math.round(f.contribution)]),
    },
    ...extra,
  };
}

async function updateSignal(id: string, patch: Record<string, unknown>) {
  const { error } = await supabase
    .from("signals")
    .update({ ...patch, updated_at: new Date().toISOString() } as never)
    .eq("id", id);
  if (error) throw new Error(error.message);
}

function restorePatch(s: Signal) {
  return {
    status: s.status,
    current_disposition: s.currentDisposition,
    linked_record_id: s.linkedRecordId,
    current_severity: s.currentSeverity,
    severity_rationale: s.severityRationale,
  };
}

/**
 * Governed disposition (Monitor / Link / No Further Action). Class A:
 * authority → signal state → audit event → disposition + snapshot, all awaited.
 * Any failure restores the signal and reports "not recorded".
 */
export async function disposeSignal(args: {
  signal: Signal;
  disposition: Exclude<Disposition, "create">;
  rationale: string;
  targetRecord?: ProblemRecord | undefined;
}): Promise<SignalResult> {
  const { signal, disposition, rationale, targetRecord } = args;
  if (!rationale.trim())
    return { ok: false, error: "A rationale is required for every disposition." };
  if (disposition === "link" && !targetRecord)
    return { ok: false, error: "Choose the Problem Record to link this signal to." };

  const { roles, rules } = await loadAuthorityContext();
  const verdict = evaluateAuthority({
    decisionType: "signal.disposition",
    record: {
      riskScore: (signal.currentSeverity ?? 1) * 20 - 20,
      financial: { exposure: signal.financialExposure } as never,
    },
    roles,
    rules,
  });
  if (!verdict.authorised) return { ok: false, error: `Not authorised: ${verdict.reason}` };

  const status =
    disposition === "monitor" ? "monitoring" : disposition === "link" ? "linked" : "closed-nfa";
  try {
    await updateSignal(signal.id, {
      status,
      current_disposition: disposition,
      linked_record_id: disposition === "link" ? targetRecord!.id : signal.linkedRecordId,
    });
  } catch {
    return { ok: false, error: NOT_RECORDED };
  }

  try {
    const actor = await getSignedInActor();
    const auditId = await recordAuditEvent({
      entityType: "signal",
      entityId: signal.id,
      recordCode: signal.code,
      eventType: "signal.dispositioned",
      action: `Signal ${signal.code} dispositioned — ${disposition}`,
      clause: "IATF 16949 10.2.3",
      detail: rationale.slice(0, 300),
      authorityRuleKey: verdict.matchedRuleKey ?? "signal.disposition",
      payload: { disposition, targetRecordId: targetRecord?.id ?? null, authorisedBy: "human" },
    });
    const { error } = await supabase.from("signal_dispositions").insert({
      signal_id: signal.id,
      disposition,
      rationale,
      target_record_id: targetRecord?.id ?? null,
      decided_by_user_id: actor.id,
      decided_by_label: actor.label,
      authority_rule_key: verdict.matchedRuleKey ?? "signal.disposition",
      audit_event_id: auditId,
      evidence_snapshot: signalSnapshot(signal, { disposition, rationale }) as never,
    });
    if (error) throw new Error(error.message);
    if (disposition === "link" && targetRecord) {
      await recordAuditEvent({
        entityId: targetRecord.id,
        recordCode: targetRecord.code,
        eventType: "record.signal_linked",
        action: `Signal ${signal.code} linked to governed record`,
        clause: "IATF 16949 10.2.3",
        detail: rationale.slice(0, 300),
        payload: { signalId: signal.id },
      });
    }
  } catch {
    await updateSignal(signal.id, restorePatch(signal)).catch(() => undefined);
    return { ok: false, error: NOT_RECORDED };
  }
  return { ok: true };
}

/**
 * Human severity assessment (1–5) with rationale. Severity 5 requires a second,
 * independent approval before it becomes the signal's current severity.
 */
export async function assessSeverity(args: {
  signal: Signal;
  severity: number;
  rationale: string;
}): Promise<SignalResult & { pendingSecondApproval?: boolean }> {
  const { signal, severity, rationale } = args;
  if (!rationale.trim()) return { ok: false, error: "A severity rationale is required." };
  const { roles, rules } = await loadAuthorityContext();
  const verdict = evaluateAuthority({
    decisionType: "signal.severity",
    record: {
      riskScore: severity * 20,
      financial: { exposure: signal.financialExposure } as never,
    },
    roles,
    rules,
  });
  if (!verdict.authorised) return { ok: false, error: `Not authorised: ${verdict.reason}` };

  const actor = await getSignedInActor();
  const { data: me } = await supabase.auth.getUser();
  // Two accounts can share a display name, so decision history names the account too.
  const assessor = me.user?.email
    ? `${actor.label || me.user.email} (${me.user.email})`
    : actor.label;
  let pending = false;
  let secondApproval = false;
  if (verdict.requiresSecondApproval) {
    const { data: priorRows } = await supabase
      .from("signal_severity_assessments")
      .select("assessed_by_user_id, severity")
      .eq("signal_id", signal.id)
      .eq("severity", severity);
    const independent = (priorRows ?? []).some(
      (r) => r.assessed_by_user_id && r.assessed_by_user_id !== me.user?.id,
    );
    pending = !independent;
    secondApproval = independent;
  }

  try {
    const auditId = await recordAuditEvent({
      entityType: "signal",
      entityId: signal.id,
      recordCode: signal.code,
      eventType: "signal.severity_assessed",
      action: `Severity ${severity} assessed on ${signal.code}${pending ? " (awaiting second approval)" : secondApproval ? " (independent second approval — confirmed)" : ""}`,
      clause: "IATF 16949 8.3.5.2",
      detail: rationale.slice(0, 300),
      authorityRuleKey: verdict.matchedRuleKey ?? "signal.severity",
      payload: {
        severity,
        aiSuggested: signal.aiSuggestedSeverity,
        pending,
        secondApproval,
        authorisedBy: "human",
      },
    });
    const { error } = await supabase.from("signal_severity_assessments").insert({
      signal_id: signal.id,
      severity,
      rationale,
      ai_suggested: signal.aiSuggestedSeverity,
      assessed_by_user_id: actor.id,
      assessed_by_label: secondApproval ? `${assessor} — independent second approval` : assessor,
      authority_rule_key: verdict.matchedRuleKey ?? "signal.severity",
      second_approval_required: pending,
      audit_event_id: auditId,
    });
    if (error) throw new Error(error.message);
    if (!pending)
      await updateSignal(signal.id, { current_severity: severity, severity_rationale: rationale });
  } catch {
    return { ok: false, error: NOT_RECORDED };
  }
  return { ok: true, pendingSecondApproval: pending };
}

/**
 * Governed Create Problem Record decision. Builds a standard 11-stage record,
 * links the originating signal and carries any verified root cause in as
 * evidence (no forced re-analysis). Class A throughout; on failure the new
 * record is removed and the signal restored.
 */
export async function createRecordFromSignal(args: {
  signal: Signal;
  rationale: string;
  existingRecords: ProblemRecord[];
  platform: string;
  title: string;
}): Promise<SignalResult & { record?: ProblemRecord }> {
  const { signal, rationale, existingRecords } = args;
  if (!rationale.trim())
    return { ok: false, error: "A rationale is required to create a Problem Record." };
  if (!signal.programId)
    return { ok: false, error: "This signal has no vehicle program to open a record against." };
  if (!signal.currentSeverity)
    return { ok: false, error: "Assess and confirm severity before creating a Problem Record." };

  const { roles, rules } = await loadAuthorityContext();
  const verdict = evaluateAuthority({
    decisionType: "signal.create",
    record: {
      riskScore: signal.currentSeverity * 20 - 20,
      financial: { exposure: signal.financialExposure } as never,
    },
    roles,
    rules,
  });
  if (!verdict.authorised) return { ok: false, error: `Not authorised: ${verdict.reason}` };

  const actor = await getSignedInActor();
  const nums = existingRecords
    .map((r) => Number(r.code.replace(/\D/g, "")))
    .filter(Number.isFinite);
  const code = `PR-${Math.max(2612, ...nums) + 1}`;
  const rootCauses = await listRootCause([signal.id]);
  const verified = rootCauses[0];

  let record = buildRecordFromSignal({
    code,
    title: args.title || signal.title,
    programId: signal.programId,
    platform: args.platform,
    component: signal.component,
    failureCause: verified ? verified.statement : "To be determined — root cause not yet verified",
    supplier: signal.supplierName,
    plant: signal.plant,
    severity: signal.currentSeverity,
    unitsInService: signal.exposureUnits,
    exposure: signal.financialExposure,
    claims: 0,
    owner: actor.label || "Quality Engineer",
    description: `${signal.summary} Opened from signal ${signal.code} by governed Create PR decision.`,
    c1000: signal.magnitudeUnit === "claims per 1,000" ? signal.magnitude : 0.5,
  });

  if (verified) {
    const rcStageIdx = record.stages.findIndex((s) => /root cause/i.test(s.name));
    const item = {
      id: `${code}-rc-${verified.id.slice(0, 6)}`,
      name: `Verified root cause carried in — ${verified.source}`,
      type: "supplier-8d" as const,
      addedAt: new Date().toISOString(),
      addedBy: verified.verifiedBy,
      summary: `${verified.statement} Verified by ${verified.verificationMethod}. Retained as evidence; re-analysis not required solely because the issue entered Broadview.`,
    };
    record = {
      ...record,
      stages: record.stages.map((s, i) =>
        i === rcStageIdx ? { ...s, evidence: [...s.evidence, item] } : s,
      ),
    };
  }

  const { error: saveErr } = await supabase.from("problem_records").insert({
    id: record.id,
    code: record.code,
    title: record.title,
    program_id: record.programId,
    risk: record.risk,
    status: record.status,
    risk_score: record.riskScore,
    exposure: record.financial.exposure,
    record: JSON.parse(JSON.stringify(record)) as never,
  });
  if (saveErr)
    return {
      ok: false,
      error: "This decision was not recorded — the Problem Record could not be saved.",
    };

  const rollback = async () => {
    await updateSignal(signal.id, restorePatch(signal)).catch(() => undefined);
    await supabase.from("record_problem_statements").delete().eq("record_id", record.id);
    await supabase.from("problem_records").delete().eq("id", record.id);
  };

  try {
    await updateSignal(signal.id, {
      status: "converted",
      current_disposition: "create",
      linked_record_id: record.id,
    });
    const auditId = await recordAuditEvent({
      entityType: "signal",
      entityId: signal.id,
      recordCode: signal.code,
      eventType: "signal.dispositioned",
      action: `Signal ${signal.code} — Create New Problem Record ${code}`,
      clause: "IATF 16949 10.2.3",
      detail: rationale.slice(0, 300),
      authorityRuleKey: verdict.matchedRuleKey ?? "signal.create_record",
      payload: { disposition: "create", recordId: record.id, authorisedBy: "human" },
    });
    await recordAuditEvent({
      entityId: record.id,
      recordCode: code,
      eventType: "record.created_from_signal",
      action: `Problem Record ${code} created from signal ${signal.code}`,
      clause: "IATF 16949 10.2.3",
      detail: rationale.slice(0, 300),
      authorityRuleKey: verdict.matchedRuleKey ?? "signal.create_record",
      payload: { signalId: signal.id, rootCauseCarried: Boolean(verified) },
    });
    const { error: dispErr } = await supabase.from("signal_dispositions").insert({
      signal_id: signal.id,
      disposition: "create",
      rationale,
      target_record_id: record.id,
      decided_by_user_id: actor.id,
      decided_by_label: actor.label,
      authority_rule_key: verdict.matchedRuleKey ?? "signal.create_record",
      audit_event_id: auditId,
      evidence_snapshot: signalSnapshot(signal, {
        disposition: "create",
        recordCode: code,
        rootCause: verified ?? null,
      }) as never,
    });
    if (dispErr) throw new Error(dispErr.message);
    await captureEvidenceSnapshot({
      record,
      stage: record.stages[0],
      decisionId: `create-${signal.id}`,
      recommendationId: `signal:${signal.id}`,
      verdict: "create",
      auditEventId: auditId,
    });
    if (verified) {
      const stage = record.stages.find((s) => /root cause/i.test(s.name));
      const ev = stage?.evidence[stage.evidence.length - 1];
      if (stage && ev) {
        await registerEvidence({
          recordId: record.id,
          recordCode: code,
          stageId: stage.id,
          evidenceRef: ev.id,
          name: ev.name,
          evidenceType: ev.type,
          owner: ev.addedBy,
          sourceSystem: signal.sourceSystemId ?? "",
          sourceReference: verified.evidenceRef,
          provenance: `Incoming verified root cause from ${verified.source}; carried from signal ${signal.code}.`,
          summary: ev.summary,
          status: "approved",
        });
      }
    }
    const { error: psErr } = await supabase.from("record_problem_statements").insert({
      record_id: record.id,
      observed_condition: signal.summary,
      expected_condition: `${signal.component} performs to specification with no customer-perceptible failure.`,
      scope: `${signal.exposureUnits.toLocaleString()} units in the exposure population.`,
      affected_product: `${signal.component}${signal.supplierName ? ` — ${signal.supplierName}` : ""}`,
      affected_process: signal.plant ? `Assembly / supply at ${signal.plant}` : "",
      affected_location: signal.plant,
      known_boundaries: "Boundaries to be confirmed during Connected Evidence.",
      severity: signal.currentSeverity,
      severity_rationale: signal.severityRationale,
      accountable_owner: actor.label,
    });
    if (psErr) throw new Error(psErr.message);
  } catch {
    await rollback();
    return { ok: false, error: NOT_RECORDED };
  }

  // Class B telemetry — non-blocking.
  if (verified) {
    void supabase
      .from("signal_root_cause_evidence")
      .update({ carried_to_record_id: record.id })
      .eq("id", verified.id)
      .then(() => undefined);
  }
  logLineage({
    entityType: "problem_record",
    entityId: record.id,
    sourceSystemId: signal.sourceSystemId ?? "synthetic-seed",
    sourceReference: signal.code,
    transformation: "Created from governed signal disposition",
    dataQualityStatus: "verified",
  });
  return { ok: true, recordId: record.id, record };
}
